// @flow

import { isEqual } from "lodash";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import queryString from "query-string";
import NProgress from "nprogress";
import Helmet from "react-helmet";
import { parse } from "date-fns";

import { fetchPosts } from "../api";
import addACFData from "../utils/addACFData";
import scrollToEl from "../utils/scrollToEl";
import GTM from "../utils/gtm";
import getContentReferenceTileProps from "../utils/getContentReferenceTileProps";
import usePageFromStore from "../hooks/usePageFromStore";
import useAsyncState from "../hooks/useAsyncState";

import HeroListing from "../components/organisms/HeroListing";
import QuickSearch from "../components/molecules/QuickSearch";
import FiltersModal from "../components/organisms/FiltersModal";
import SearchModal from "../components/organisms/SearchModal";
import Tile from "../components/molecules/Tile";
import Button from "../components/atoms/Button";
import Pagination from "../components/molecules/Pagination";
import Icon from "../components/atoms/Icon";
import ShopFooter from "../components/organisms/ShopFooter";
import LinkHandler from "../components/atoms/LinkHandler";
import Loader from "../components/atoms/Loader";
import TileCarousel from "../components/molecules/TileCarousel";
import Leaderboard from "../components/organisms/Leaderboard";

type PropsType = {};

const Listing = (props: PropsType): any => {
    const POSTS_PER_PAGE = 16; // 16
    const PAGINATION_WIDTH = 2;

    const ListingContentRef = React.useRef();
    const history = useHistory();
    const default_tile_image = useSelector(
        (state: any): any => state.options.default_tile_image
    );
    const [state, actions] = useAsyncState();
    const { page } = usePageFromStore();
    const [initialSetup, setInitialSetup] = React.useState(false);
    const [initialFetchComplete, setInitialFetchComplete] =
        React.useState(false);
    const [pathname] = React.useState(document.location.pathname);
    const [params, setParams] = useState(null);
    const [posts, setPosts] = React.useState([]);
    const [headers, setHeaders] = React.useState({});
    const [activeFilterModal, setActiveFilterModal] = React.useState(false);
    const [activeSearchModal, setActiveSearchModal] = React.useState(false);

    const totalPages = parseInt(headers["x-wp-totalpages"]) || 1;
    const queryParamProps = queryString.parse(document.location.search);
    const activePage = Number(queryParamProps.page_num) || 1;
    const post_type =
        page && page.listing && page.listing.endpoint
            ? page.listing.endpoint.split("/")[
                  page.listing.endpoint.split("/").length - 1
              ]
            : "";
    const adTiles = page.acf && page.acf.ad_tiles;
    const showAdTile = adTiles && !queryParamProps.search;

    // initial mount useEffect, set default filters/params
    useEffect(() => {
        applyFilters(queryParamProps, true);
    }, []); // eslint-disable-line

    // new params/pathname - set active params
    useEffect(() => {
        if (
            !isEqual(queryParamProps, params) &&
            pathname === document.location.pathname
        ) {
            if (!Object.keys(queryParamProps).length) {
                // no new query params, reset and apply new default filters
                setParams(null);
                applyFilters(queryParamProps, true);
            } else {
                setParams(queryParamProps);
            }
        }
    }, [pathname, queryParamProps, params]); // eslint-disable-line

    // fetch listing api on params useState change
    useEffect(() => {
        if (params && initialSetup) {
            NProgress.start();
            actions.request();

            fetchPosts({
                endpoint: page.listing.endpoint,
                postType: post_type,
                perPage: showAdTile ? POSTS_PER_PAGE - 1 : POSTS_PER_PAGE,
                queryParms: params,
                // offset: !showAdTile ? POSTS_PER_PAGE * (activePage - 1) - 1 : 0,
            })
                .then(({ data, headers }: any) => {
                    if (!data.code) {
                        if (showAdTile && data.length > 3) {
                            data.splice(3, 0, adTiles);
                        }

                        setPosts(data);
                        setHeaders(headers);
                    } else {
                        setPosts([]);
                        setHeaders({});
                    }
                })
                .then(actions.success)
                .catch(actions.fail)
                .finally(() => {
                    NProgress.done();

                    if (initialFetchComplete) {
                        scrollToTop();
                    }

                    setInitialFetchComplete(true);

                    if (params.search) {
                        // log GTM search event
                        GTM.push({
                            event: "site_search",
                            search_term: params.search,
                            page_type: `${window.location.pathname}`,
                        });
                    }
                });
        }
    }, [params, initialSetup]); // eslint-disable-line

    const applyFilters = (queryParams, firstLoad) => {
        let defaultParams = {};

        if (page.listing && page.listing.filters) {
            page.listing.filters.forEach(filter => {
                filter.options.forEach(filterOption => {
                    if (filterOption.default) {
                        if (!queryParams || !queryParams[filter.option_key]) {
                            defaultParams[filter.option_key] = filterOption.key;
                        }
                    }
                });
            });
        }

        const finalParams = new URLSearchParams({
            ...defaultParams,
            ...queryParams,
        });

        if (firstLoad) {
            // first load so history replace with default params
            history.replace({
                pathname: document.location.pathname,
                search: finalParams.toString(),
            });
        } else {
            history.push({
                pathname: document.location.pathname,
                search: finalParams.toString(),
            });
        }

        setInitialSetup(true);
    };

    const removeFilterByValue = (filterOption, filter) => {
        let updatedParams = { ...params };

        const newParamKeyParts = updatedParams[filter.option_key]
            .split(",")
            .filter(arrayItem => arrayItem !== filterOption.key.toString());
        updatedParams[filter.option_key] = newParamKeyParts.join(",");

        // delete the query param key if value is now empty
        if (!updatedParams[filter.option_key]) {
            delete updatedParams[filter.option_key];
        }

        applyFilters(updatedParams);
    };

    const scrollToTop = () => {
        if (ListingContentRef.current) {
            scrollToEl(ListingContentRef.current, 500, -40);

            setTimeout(() => {
                scrollToEl(ListingContentRef.current, 500, -30);
            }, 100);
        }
    };

    const getTotalFiltersApplied = (): number => {
        let filtersApplied = 0;

        Object.keys(queryParamProps).forEach((paramKey: string): any => {
            page.listing.filters.forEach(listingFilter => {
                if (
                    !listingFilter.is_sorting &&
                    listingFilter.option_key === paramKey &&
                    queryParamProps[paramKey]
                ) {
                    filtersApplied =
                        filtersApplied +
                        queryParamProps[paramKey].split(",").length;
                }
            });
        });
        return filtersApplied;
    };

    const isFilterOptionActive = (optionKey: string, filterOption: any) => {
        const initialParamKeyValuesArray = decodeURIComponent(
            queryParamProps[optionKey]
        ).split(",");

        const filterActive = queryParamProps[optionKey]
            ? initialParamKeyValuesArray.includes(
                  encodeURIComponent(filterOption.key)
              )
            : filterOption.default || false;

        return filterActive;
    };

    const leaderboardTop = page.acf.top_leaderboard_ad
        ? getContentReferenceTileProps(addACFData(page.acf.top_leaderboard_ad))
        : null;

    const leaderboardBottom = page.acf.bottom_leaderboard_ad
        ? getContentReferenceTileProps(
              addACFData(page.acf.bottom_leaderboard_ad)
          )
        : null;

    const getHeroTheme = () => {
        switch (post_type) {
            case "clubs":
                return "pink";
            case "events":
                return "purple";
            case "products":
                return "red";
            default:
                return "pink";
        }
    };

    const renderPost = (
        post: any,
        featured: boolean,
        index: number
    ): React.Node => {
        const mappedTileProps = post.acf
            ? getContentReferenceTileProps(post)
            : post.tile_reference
            ? getContentReferenceTileProps(addACFData(post.tile_reference))
            : null;
        let adTiles = null;
        if (Array.isArray(post)) {
            adTiles = post
                .map((adTile, adTileIndex): React.Node => {
                    const referenceContent = getContentReferenceTileProps(
                        addACFData(adTile.ad_tile_references)
                    );
                    if (!referenceContent) return null;
                    return <Tile {...referenceContent} />;
                })
                .filter(Boolean);
        }

        return (
            <div key={index} className={`col-xs-6 col-sm-4 col-lg-3`}>
                <div className="listing__search-result">
                    {showAdTile && adTiles ? (
                        <TileCarousel duration={5000} tiles={adTiles} />
                    ) : (
                        <Tile {...mappedTileProps} />
                    )}
                </div>
            </div>
        );
    };

    const renderPostsSchema = () => {
        if (!posts && !posts.length) {
            return null;
        }

        const postsSchemaArray = posts.map(post => {
            if (!post || !post.type) {
                return {};
            }

            switch (post.type) {
                case "event":
                    return {
                        startDate: parse(
                            post.acf.start_date,
                            "dd/MM/yyyy h:mm aa",
                            new Date()
                        ),
                        endDate: parse(
                            post.acf.end_date,
                            "dd/MM/yyyy h:mm aa",
                            new Date()
                        ),
                        name: post.title,
                        url: post.url,
                        image:
                            post.acf.hero &&
                            post.acf.hero.media &&
                            post.acf.hero.media[0] &&
                            post.acf.hero.media[0].image &&
                            post.acf.hero.media[0].image.sizes.medium_large
                                ? post.acf.hero.media[0].image.sizes
                                      .medium_large
                                : default_tile_image.sizes.medium_large,
                        eventStatus:
                            page.acf.open_state === "cancelled"
                                ? "https://schema.org/EventCancelled"
                                : "https://schema.org/EventScheduled",
                        location: {
                            address: {
                                addressCountry: "",
                                addressLocality: "",
                                addressRegion: "",
                                streetAddress: post.event_location || "UTS",
                                postalCode: "",
                                "@type": "PostalAddress",
                                contactType: "customer service",
                            },
                            // geo: {
                            //     latitude: "-33.8325315",
                            //     "@type": "GeoCoordinates",
                            //     longitude: "151.0861678",
                            // },
                            "@type": "Place",
                            name: post.event_location || "UTS",
                        },
                        eventAttendanceMode:
                            "https://schema.org/OfflineEventAttendanceMode",
                        "@context": "http://schema.org",
                        "@type": "Event",
                        description: post.acf.excerpt,
                    };
                default:
                    return {};
            }
        });

        return JSON.stringify(postsSchemaArray);
    };

    return (
        <div className={`listing ${state.loading ? "is-loading" : ""}`}>
            <Helmet>
                <script type="application/ld+json">
                    {renderPostsSchema()}
                </script>
            </Helmet>

            <HeroListing
                heading={page.post_title}
                description={page.acf.description}
                ctaInfo={page.acf.cta_info}
                ctas={page.acf.ctas}
                theme={getHeroTheme()}
            />

            {/* Leaderboard Top */}
            {leaderboardTop && (
                <Leaderboard
                    useContainer={true}
                    additionalClasses="top"
                    imageDesktop={leaderboardTop.image_desktop}
                    imageMobile={leaderboardTop.image_mobile}
                    alt={leaderboardTop.title}
                    link={leaderboardTop.url}
                    pageType="listing"
                    position="top"
                />
            )}

            <div className="listing__inner" ref={ListingContentRef}>
                <div className="listing__container container-fluid">
                    <div className="listing__controls">
                        <div className="listing__search show-for-sm">
                            <QuickSearch
                                postType={page.listing.type}
                                defaultValue={queryParamProps.search || ""}
                                placeholder={`Search ${
                                    page.listing.type === "product"
                                        ? "shop & hire"
                                        : page.listing.type + "s"
                                }`}
                                inline={true}
                                onSubmit={value => {
                                    history.push(
                                        `${document.location.pathname}?search=${value}`
                                    );
                                }}
                            />
                        </div>

                        {/* Mobile search button/modal */}
                        <Button
                            className="listing__filter-btn hide-for-sm"
                            iconBefore="search"
                            inline={true}
                            onClick={() => setActiveSearchModal(true)}
                        >
                            Search
                        </Button>
                        <SearchModal
                            heading={`Search ${page.listing.type}s...`}
                            postType={page.listing.type}
                            defaultValue={queryParamProps.search}
                            focusSearch={true}
                            placeholder={`Search ${
                                page.listing.type === "products"
                                    ? ""
                                    : page.listing.type
                            }s...`}
                            active={activeSearchModal}
                            onClose={() => setActiveSearchModal(false)}
                            onSubmit={value => {
                                history.push(
                                    `${document.location.pathname}?search=${value}`
                                );
                            }}
                        />

                        {/* Filters button/modal */}
                        <Button
                            className="listing__filter-btn"
                            iconBefore="filter"
                            inline={true}
                            onClick={() => setActiveFilterModal(true)}
                            counter={
                                getTotalFiltersApplied() &&
                                getTotalFiltersApplied() > 0
                                    ? getTotalFiltersApplied()
                                    : null
                            }
                        >
                            Filter & sort
                        </Button>
                        <FiltersModal
                            heading={"Filter & sort"}
                            active={activeFilterModal}
                            filters={page.listing.filters}
                            onClose={() => setActiveFilterModal(false)}
                        />
                    </div>

                    {/* Applied filter tags and clear button */}
                    {getTotalFiltersApplied() ? (
                        <div className="listing__applied-filters">
                            {page.listing.filters.map(filter => {
                                if (!filter.is_sorting) {
                                    return filter.options.map(
                                        (
                                            filterOption: {},
                                            index: number
                                        ): React.Node => {
                                            if (
                                                isFilterOptionActive(
                                                    filter.option_key,
                                                    filterOption
                                                )
                                            ) {
                                                return (
                                                    <div
                                                        key={index}
                                                        className="listing__applied-filters__tag"
                                                    >
                                                        {filterOption.label}
                                                        <button
                                                            className="listing__applied-filters__tag__btn"
                                                            onClick={() =>
                                                                removeFilterByValue(
                                                                    filterOption,
                                                                    filter
                                                                )
                                                            }
                                                        >
                                                            <Icon name="close" />
                                                        </button>
                                                    </div>
                                                );
                                            } else {
                                                return null;
                                            }
                                        }
                                    );
                                } else {
                                    return null;
                                }
                            })}

                            <Button
                                type="button"
                                inline={true}
                                styles={["extra-small"]}
                                onClick={() => applyFilters({})}
                            >
                                Clear all
                            </Button>
                        </div>
                    ) : null}

                    {state.loading ? (
                        <Loader
                            className="center-xs"
                            inline={true}
                            active={true}
                            style={{
                                margin: "40px 0",
                            }}
                        />
                    ) : null}

                    {/* No results found */}
                    {!state.loading && posts && posts.length === 0 ? (
                        <>
                            No results found. Try searching something else, or
                            return to all{" "}
                            <LinkHandler
                                to={`${document.location.pathname}`}
                                className="underline"
                            >
                                {page.listing.type}s
                            </LinkHandler>
                            .
                        </>
                    ) : null}

                    {/* Listing items */}
                    {!state.loading && posts && posts.length ? (
                        <>
                            <div className="listing__items row">
                                {posts.map((post: any, index: number): any =>
                                    renderPost(post, false, index)
                                )}
                            </div>

                            {/* Listing pagination */}
                            <div className="listing__pagination">
                                {totalPages > 1 ? (
                                    <Pagination
                                        ellipsisWidth={PAGINATION_WIDTH}
                                        pages={totalPages}
                                        activePage={activePage}
                                    />
                                ) : null}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            {/* Product listing shop footer */}
            {page.listing.type === "product" ? <ShopFooter /> : null}

            {/* Leaderboard Bottom */}
            {leaderboardBottom && (
                <Leaderboard
                    useContainer={true}
                    imageDesktop={leaderboardBottom.image_desktop}
                    imageMobile={leaderboardBottom.image_mobile}
                    alt={leaderboardBottom.title}
                    link={leaderboardBottom.url}
                    pageType="listing"
                    position="bottom"
                />
            )}
        </div>
    );
};

export default Listing;
